/**
 * Google Drive open/save for tools (B-0319 phase 1).
 *
 * Privacy shape, on purpose:
 *  - Scope is `drive.file` only (non-sensitive): the app sees only files the person picks in the Google Picker
 *    or files this app itself creates. No Drive-wide listing.
 *  - The access token lives in this module's memory only. Never localStorage, never a cookie, never our server,
 *    no refresh token (the token client's popup flow does not issue one). Closing the tab forgets it.
 *  - File bytes go browser <-> Google directly (fetch to www.googleapis.com). Nothing passes through our server.
 *  - Google's scripts (accounts.google.com/gsi/client, apis.google.com/js/api.js) load only when the person
 *    clicks an Open/Save button, never on page load.
 *  - Without VITE_GOOGLE_CLIENT_ID and VITE_GOOGLE_API_KEY, isGoogleConfigured() is false and the UI hides.
 */

export const GOOGLE_DRIVE_SCOPE = "https://www.googleapis.com/auth/drive.file";
export const GSI_SRC = "https://accounts.google.com/gsi/client";
export const GAPI_SRC = "https://apis.google.com/js/api.js";
/** Above this size the person is warned that the browser may be slow or run out of memory. */
export const LARGE_FILE_WARN_BYTES = 50 * 1024 * 1024;
/** Hard ceiling for fetching a Drive file into browser memory. */
export const MAX_OPEN_BYTES = 1024 * 1024 * 1024;
/** Up to this size one multipart request is used; above it a chunked resumable upload. */
export const MULTIPART_MAX_BYTES = 5 * 1024 * 1024;
/** Resumable chunks must be a multiple of 256 KiB. */
export const UPLOAD_CHUNK_BYTES = 8 * 1024 * 1024;

export type GoogleErrorCode =
  | "not-configured"
  | "popup-blocked"
  | "cancelled"
  | "denied"
  | "script-load"
  | "auth"
  | "network"
  | "too-large"
  | "api";

export class GoogleCloudError extends Error {
  code: GoogleErrorCode;
  status?: number;
  constructor(code: GoogleErrorCode, message?: string, status?: number) {
    super(message ?? code);
    this.name = "GoogleCloudError";
    this.code = code;
    this.status = status;
  }
}

export interface GoogleConfig {
  clientId: string;
  apiKey: string;
  /** Google Cloud project number; lets the Picker pass it to Drive so drive.file access is granted for picked files. */
  appId?: string;
}

export function getGoogleConfig(): GoogleConfig | null {
  // Written out in full (not through a variable) so Vite can replace each one at build time.
  const clientId = String(import.meta.env.VITE_GOOGLE_CLIENT_ID ?? "").trim();
  const apiKey = String(import.meta.env.VITE_GOOGLE_API_KEY ?? "").trim();
  if (!clientId || !apiKey) return null;
  const appId = String(import.meta.env.VITE_GOOGLE_APP_ID ?? "").trim();
  return { clientId, apiKey, appId: appId || undefined };
}

export const isGoogleConfigured = (): boolean => getGoogleConfig() !== null;

// ---- script loading -------------------------------------------------------------------------------------------

const scriptLoads = new Map<string, Promise<void>>();

export function loadScript(src: string): Promise<void> {
  const cached = scriptLoads.get(src);
  if (cached) return cached;
  const p = new Promise<void>((resolve, reject) => {
    if (typeof document === "undefined") return reject(new GoogleCloudError("script-load"));
    const el = document.createElement("script");
    el.src = src;
    el.async = true;
    el.onload = () => resolve();
    el.onerror = () => reject(new GoogleCloudError("script-load", `Could not load ${src}`));
    document.head.appendChild(el);
  });
  // A failed load (blocker, offline) is not cached, so the next click tries again.
  p.catch(() => scriptLoads.delete(src));
  scriptLoads.set(src, p);
  return p;
}

// Minimal shapes of the parts of Google's scripts used here.
interface TokenResponse { access_token?: string; expires_in?: number | string; error?: string; scope?: string }
interface TokenClient { requestAccessToken: (o?: { prompt?: string }) => void }
interface GoogleGlobal {
  accounts: {
    oauth2: {
      initTokenClient: (c: {
        client_id: string;
        scope: string;
        callback: (r: TokenResponse) => void;
        error_callback?: (e: { type?: string; message?: string }) => void;
      }) => TokenClient;
      hasGrantedAllScopes?: (r: TokenResponse, ...scopes: string[]) => boolean;
      revoke?: (token: string, done?: () => void) => void;
    };
  };
  picker: any;
}
const g = (): GoogleGlobal | undefined => (globalThis as unknown as { google?: GoogleGlobal }).google;
const gapiGlobal = () => (globalThis as unknown as { gapi?: { load: (n: string, cb: { callback: () => void; onerror?: () => void }) => void } }).gapi;

// ---- sign-in (memory-only token) -------------------------------------------------------------------------------

let token: { value: string; expiresAt: number } | null = null;

export function hasValidToken(now = Date.now()): boolean {
  return !!token && token.expiresAt - 60_000 > now;
}

/** Forgets the token (and asks Google to revoke it, best effort). The Disconnect action and tests use this. */
export function signOutGoogle(): void {
  const t = token?.value;
  token = null;
  try { if (t) g()?.accounts.oauth2.revoke?.(t); } catch { /* best effort */ }
}

/** Resets module state; for tests. */
export function __resetGoogleForTests(): void {
  token = null;
  scriptLoads.clear();
}

/**
 * Returns a valid access token, opening Google's sign-in popup if there is none. Must be called from a click
 * handler (popup blockers). Reuses the in-memory token for the rest of the page's life, until it expires.
 */
export async function ensureGoogleToken(): Promise<string> {
  const cfg = getGoogleConfig();
  if (!cfg) throw new GoogleCloudError("not-configured");
  if (hasValidToken()) return token!.value;
  await loadScript(GSI_SRC);
  const oauth2 = g()?.accounts?.oauth2;
  if (!oauth2) throw new GoogleCloudError("script-load", "Google sign-in did not load");
  return new Promise<string>((resolve, reject) => {
    let client: TokenClient;
    try {
      client = oauth2.initTokenClient({
        client_id: cfg.clientId,
        scope: GOOGLE_DRIVE_SCOPE,
        callback: (resp) => {
          if (resp.error || !resp.access_token) {
            const denied = resp.error === "access_denied" || resp.error === "consent_required";
            return reject(new GoogleCloudError(denied ? "denied" : "auth", resp.error));
          }
          // The person may untick the Drive permission on the consent screen: then there is no usable scope.
          if (oauth2.hasGrantedAllScopes && !oauth2.hasGrantedAllScopes(resp, GOOGLE_DRIVE_SCOPE)) {
            return reject(new GoogleCloudError("denied", "scope not granted"));
          }
          const secs = Number(resp.expires_in) || 3600;
          token = { value: resp.access_token, expiresAt: Date.now() + secs * 1000 };
          resolve(resp.access_token);
        },
        error_callback: (e) => {
          const t = e?.type;
          reject(new GoogleCloudError(t === "popup_failed_to_open" ? "popup-blocked" : t === "popup_closed" ? "cancelled" : "auth", t));
        },
      });
      client.requestAccessToken({ prompt: "" });
    } catch (e) {
      reject(e instanceof GoogleCloudError ? e : new GoogleCloudError("auth", String(e)));
    }
  });
}

// ---- Picker ---------------------------------------------------------------------------------------------------

export interface PickedDriveFile { id: string; name: string; mimeType: string; sizeBytes?: number }

const EXT_MIME: Record<string, string[]> = {
  pdf: ["application/pdf"],
  png: ["image/png"],
  jpg: ["image/jpeg"], jpeg: ["image/jpeg"],
  gif: ["image/gif"], webp: ["image/webp"], bmp: ["image/bmp"], svg: ["image/svg+xml"],
  heic: ["image/heic"], avif: ["image/avif"],
  txt: ["text/plain"], csv: ["text/csv"], json: ["application/json"], md: ["text/markdown"], html: ["text/html"],
  zip: ["application/zip"],
  mp3: ["audio/mpeg"], wav: ["audio/wav", "audio/x-wav"], m4a: ["audio/mp4", "audio/x-m4a"],
  mp4: ["video/mp4"], mov: ["video/quicktime"], webm: ["video/webm"],
  docx: ["application/vnd.openxmlformats-officedocument.wordprocessingml.document", "application/vnd.google-apps.document"],
  xlsx: ["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", "application/vnd.google-apps.spreadsheet"],
  pptx: ["application/vnd.openxmlformats-officedocument.presentationml.presentation", "application/vnd.google-apps.presentation"],
  epub: ["application/epub+zip"],
};
const IMAGE_FAMILY = ["image/png", "image/jpeg", "image/gif", "image/webp", "image/bmp", "image/svg+xml", "image/heic", "image/avif", "image/tiff"];

/**
 * Turns an <input accept> string into the exact MIME types the Picker can filter by. Returns undefined (no
 * filter, everything shown) when any token cannot be mapped, so a file the tool takes is never hidden. Files
 * picked anyway are checked again by the drop zone's own accept check.
 */
export function acceptToMimeTypes(accept: string | undefined): string[] | undefined {
  if (!accept) return undefined;
  const tokens = accept.split(",").map((t) => t.trim().toLowerCase()).filter(Boolean);
  if (tokens.length === 0 || tokens.includes("*") || tokens.includes("*/*")) return undefined;
  const out = new Set<string>();
  for (const t of tokens) {
    if (t === "image/*") IMAGE_FAMILY.forEach((m) => out.add(m));
    else if (t.startsWith(".")) {
      const m = EXT_MIME[t.slice(1)];
      if (!m) return undefined;
      m.forEach((x) => out.add(x));
    } else if (t.endsWith("/*")) return undefined;
    else out.add(t);
  }
  return out.size ? Array.from(out) : undefined;
}

async function loadPicker(): Promise<void> {
  await loadScript(GAPI_SRC);
  const gapi = gapiGlobal();
  if (!gapi) throw new GoogleCloudError("script-load", "Google Picker did not load");
  await new Promise<void>((resolve, reject) => {
    gapi.load("picker", { callback: () => resolve(), onerror: () => reject(new GoogleCloudError("script-load")) });
  });
}

/** Opens the Google Picker. Resolves with the chosen files, or [] when the person closes it without choosing. */
export async function pickDriveFiles(accessToken: string, opts: { multiple?: boolean; accept?: string } = {}): Promise<PickedDriveFile[]> {
  const cfg = getGoogleConfig();
  if (!cfg) throw new GoogleCloudError("not-configured");
  await loadPicker();
  const picker = g()?.picker;
  if (!picker) throw new GoogleCloudError("script-load");
  return new Promise<PickedDriveFile[]>((resolve) => {
    const view = new picker.DocsView(picker.ViewId.DOCS).setIncludeFolders(true).setSelectFolderEnabled(false);
    const mimes = acceptToMimeTypes(opts.accept);
    // Google-native documents are only shown when the tool's accept list could take their export.
    if (mimes) view.setMimeTypes(mimes.join(","));
    const b = new picker.PickerBuilder()
      .addView(view)
      .setOAuthToken(accessToken)
      .setDeveloperKey(cfg.apiKey)
      .setOrigin(window.location.protocol + "//" + window.location.host)
      .setCallback((data: any) => {
        const action = data?.[picker.Response.ACTION];
        if (action === picker.Action.PICKED) {
          const docs: any[] = data[picker.Response.DOCUMENTS] ?? [];
          resolve(docs.map((d) => ({
            id: String(d[picker.Document.ID]),
            name: String(d[picker.Document.NAME] ?? "file"),
            mimeType: String(d[picker.Document.MIME_TYPE] ?? ""),
            sizeBytes: typeof d[picker.Document.SIZE_BYTES] === "number" ? d[picker.Document.SIZE_BYTES] : undefined,
          })));
        } else if (action === picker.Action.CANCEL) {
          resolve([]);
        }
      });
    if (cfg.appId) b.setAppId(cfg.appId);
    if (opts.multiple) b.enableFeature(picker.Feature.MULTISELECT_ENABLED);
    b.build().setVisible(true);
  });
}

// ---- download (Drive -> browser) -------------------------------------------------------------------------------

const EXPORTS: Record<string, { mime: string; ext: string }> = {
  "application/vnd.google-apps.document": { mime: "application/vnd.openxmlformats-officedocument.wordprocessingml.document", ext: ".docx" },
  "application/vnd.google-apps.spreadsheet": { mime: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", ext: ".xlsx" },
  "application/vnd.google-apps.presentation": { mime: "application/vnd.openxmlformats-officedocument.presentationml.presentation", ext: ".pptx" },
  "application/vnd.google-apps.drawing": { mime: "image/png", ext: ".png" },
};

async function ok(res: Response): Promise<Response> {
  if (res.ok) return res;
  if (res.status === 401) { token = null; throw new GoogleCloudError("auth", "token rejected", 401); }
  if (res.status === 403 || res.status === 404) throw new GoogleCloudError("denied", "no access to that file", res.status);
  throw new GoogleCloudError("api", `Google Drive answered ${res.status}`, res.status);
}

const net = async <T>(run: () => Promise<T>): Promise<T> => {
  try { return await run(); } catch (e) {
    if (e instanceof GoogleCloudError) throw e;
    throw new GoogleCloudError("network", String(e));
  }
};

/** Fetches a picked file straight from Google into a File. Throws "too-large" above MAX_OPEN_BYTES. */
export async function downloadDriveFile(f: PickedDriveFile, accessToken: string): Promise<File> {
  if (f.sizeBytes && f.sizeBytes > MAX_OPEN_BYTES) throw new GoogleCloudError("too-large");
  const exp = EXPORTS[f.mimeType];
  const id = encodeURIComponent(f.id);
  const url = exp
    ? `https://www.googleapis.com/drive/v3/files/${id}/export?mimeType=${encodeURIComponent(exp.mime)}`
    : f.mimeType.startsWith("application/vnd.google-apps.")
      ? ""
      : `https://www.googleapis.com/drive/v3/files/${id}?alt=media`;
  if (!url) throw new GoogleCloudError("api", "That Google item cannot be downloaded as a file");
  const res = await net(async () => ok(await fetch(url, { headers: { Authorization: `Bearer ${accessToken}` } })));
  const blob = await net(() => res.blob());
  if (blob.size > MAX_OPEN_BYTES) throw new GoogleCloudError("too-large");
  const name = exp && !f.name.toLowerCase().endsWith(exp.ext) ? f.name + exp.ext : f.name;
  return new File([blob], name, { type: exp?.mime ?? (f.mimeType || blob.type) });
}

// ---- upload (browser -> Drive) ---------------------------------------------------------------------------------

export interface SavedDriveFile { id: string; name: string; webViewLink?: string }

/**
 * Saves a result to the person's Drive (My Drive root; drive.file lets the app create files, nothing more).
 * Up to 5 MB: one multipart request. Larger: resumable upload in 8 MiB chunks.
 */
export async function uploadToDrive(blob: Blob, name: string, accessToken: string, onProgress?: (fraction: number) => void): Promise<SavedDriveFile> {
  const mimeType = blob.type || "application/octet-stream";
  const fields = "id,name,webViewLink";
  const auth = { Authorization: `Bearer ${accessToken}` };
  if (blob.size <= MULTIPART_MAX_BYTES) {
    const boundary = "fdp" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    const body = new Blob([
      `--${boundary}\r\nContent-Type: application/json; charset=UTF-8\r\n\r\n${JSON.stringify({ name, mimeType })}\r\n`,
      `--${boundary}\r\nContent-Type: ${mimeType}\r\n\r\n`,
      blob,
      `\r\n--${boundary}--`,
    ]);
    const res = await net(async () => ok(await fetch(`https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=${fields}`, {
      method: "POST",
      headers: { ...auth, "Content-Type": `multipart/related; boundary=${boundary}` },
      body,
    })));
    onProgress?.(1);
    return (await net(() => res.json())) as SavedDriveFile;
  }
  // Resumable: start a session, then send chunks.
  const start = await net(async () => ok(await fetch(`https://www.googleapis.com/upload/drive/v3/files?uploadType=resumable&fields=${fields}`, {
    method: "POST",
    headers: { ...auth, "Content-Type": "application/json; charset=UTF-8", "X-Upload-Content-Type": mimeType, "X-Upload-Content-Length": String(blob.size) },
    body: JSON.stringify({ name, mimeType }),
  })));
  const session = start.headers.get("Location");
  if (!session) throw new GoogleCloudError("api", "Google did not start an upload session");
  let offset = 0;
  while (offset < blob.size) {
    const end = Math.min(offset + UPLOAD_CHUNK_BYTES, blob.size);
    const res = await net(() => fetch(session, {
      method: "PUT",
      headers: { "Content-Range": `bytes ${offset}-${end - 1}/${blob.size}` },
      body: blob.slice(offset, end),
    }));
    if (res.status === 308) { offset = end; onProgress?.(offset / blob.size); continue; }
    await ok(res);
    onProgress?.(1);
    return (await net(() => res.json())) as SavedDriveFile;
  }
  throw new GoogleCloudError("api", "Upload ended without a final answer");
}
